import { useQueries } from '@tanstack/react-query';
import { EmployerDto } from '@zorro/clients';
import { ALL_EMPLOYEES_CLASS_NAME } from '@zorro/common-utils';
import { Select, SelectProps } from '@zorro/design-system/v2/Select';
import { FormErrorMessage } from '@zorro/web/design-system';
import { callEndpoint } from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import {
  FieldPath,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

type EmployeeCustomAttributesProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> & {
  employerId: EmployerDto['id'];
  classDefinitionYears: number[];
  selectProps?: SelectProps;
  'data-testid'?: string;
};

export function EmployeeCustomAttributes<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  employerId,
  classDefinitionYears,
  selectProps,
  'data-testid': dataTestId,
  ...formField
}: EmployeeCustomAttributesProps<TFieldValues, TName>) {
  const {
    field,
    formState: { errors },
  } = useController(formField);

  const customAttributeQueries = useMemo(() => {
    return classDefinitionYears.map((year) => ({
      queryKey: [
        'allowanceModelControllerGetCustomAttributesForEmployerByYear',
        employerId,
        year,
      ],
      queryFn: async () =>
        await callEndpoint({
          method:
            'allowanceModelControllerGetCustomAttributesForEmployerByYear',
          params: [employerId, year],
        }),
    }));
  }, [employerId, classDefinitionYears]);

  const customAttributeResults = useQueries({
    queries: customAttributeQueries,
  });

  const allCustomAttributes = customAttributeResults
    .flatMap((query) => query.data || [])
    .filter((attribute) => attribute !== ALL_EMPLOYEES_CLASS_NAME);

  const uniqueAttributes = Array.from(new Set(allCustomAttributes));

  const selectData = uniqueAttributes.map((attribute) => ({
    value: attribute,
    label: attribute,
  }));

  return (
    <>
      <Select
        {...field}
        placeholder="Select employee category"
        data={selectData}
        label="Custom class category"
        required
        {...selectProps}
        data-testid={dataTestId}
      />

      <FormErrorMessage fieldName={formField.name} errors={errors} />
    </>
  );
}
